import React, { useEffect, useLayoutEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { capitalize, keyBy, mapValues, startCase, toUpper } from "lodash";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import client from "@/client";
import { AddIconButton } from "@/components/IconButtons";
import { graphql } from "@/gql";
import {
  ClientMedicalHistoryFragment,
  ClientPregnancyHistoryFragment,
  DiseaseKind,
  Gender,
  GestationalKind,
  Maybe,
  Profile,
  UpdateMedicalHistoryInput,
  UpdatePregnancyHistoryInput,
} from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { useSnackBar } from "@/hooks/useSnackBar";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { apply } from "@/utils/functions";
import { humanize } from "@/utils/string";

import PregnancyHistoryFields from "./PregnancyHistoryFields";

const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: theme.spacing(24, 56),
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(2, 8),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalSubheaderText: {
    fontSize: "18px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  save: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: "24px",
    marginTop: "28px",
  },
  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
  },
  checkboxLabel: {
    color: colors.gray,
  },
  chipWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
  },
  chip: {
    fontSize: "12px",
  },
  searchBar: {
    height: 8,
  },
  list: {
    maxHeight: 100,
    overflow: "auto",
  },
  medicalChip: {
    marginRight: 4,
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    width: "100%",
    marginTop: "16px",
  },
});

const StyledFormControl = withStyles(FormControl, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    marginTop: "16px",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});

const StyledInputLabel = withStyles(InputLabel, {
  root: {
    paddingRight: 5,
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      background: colors.white,
    },
  },
});

type EditMedicalHistoryModalProps = {
  visible: boolean | undefined;
  onClose: () => void;
  gender: Profile["gender"] | undefined;
  medicalHistory: Maybe<ClientMedicalHistoryFragment> | undefined;
  pregnancyHistory: Maybe<ClientPregnancyHistoryFragment> | undefined;
};

const diseaseTypes: { [id: string]: string } = {
  ACUTE_LYMPHOBLASTIC_LEUKAEMIA_CHILDREN:
    "Acute lymphoblastic leukaemia: Children",
  ACUTE_LYMPHOBLASTIC_LEUKAEMIA_TEENAGERS_AND_YOUNG_ADULTS:
    "Acute lymphoblastic leukaemia: Teenagers and young adults",
  ACUTE_MYELOID_LEUKAEMIA_CHILDREN: "Acute myeloid leukaemia: Children",
  ACUTE_MYELOID_LEUKAEMIA_TEENAGERS_AND_YOUNG_ADULTS:
    "Acute myeloid leukaemia: Teenagers and young adults",
  ADDISONS_DISEASE: "Addison's disease",
  ALCOHOL_RELATED_LIVER_DISEASE: "Alcohol-related liver disease",
  ALZHEIMERS_DISEASE: "Alzheimer's disease",
  ATTENTION_DEFICIT_HYPERACTIVITY_DISORDER:
    "Attention deficit hyperactivity disorder (ADHD)",
  AUTISTIC_SPECTRUM_DISORDER: "Autistic spectrum disorder (ASD)",
  BILE_DUCT_CANCER: "Bile duct cancer (cholangiocarcinoma)",
  BLOOD_POISONING: "Blood poisoning (sepsis)",
  BONE_CANCER_TEENAGERS_AND_YOUNG_ADULTS:
    "Bone cancer: Teenagers and young adults",
  BRAIN_TUMOURS_TEENAGERS_AND_YOUNG_ADULTS:
    "Brain tumours: Teenagers and young adults",
  BREAST_CANCER_FEMALE: "Breast cancer (female)",
  BREAST_CANCER_MALE: "Breast cancer (male)",
  CORONAVIRUS: "Coronavirus (COVID-19)",
  CROHNS_DISEASE: "Crohn's disease",
  DIZZINESS: "Dizziness (Lightheadedness)",
  DOWNS_SYNDROME: "Down's syndrome",
  DYSPHAGIA: "Dysphagia (swallowing problems)",
  EARWAX_BUILD_UP: "Earwax build-up",
  ERECTILE_DYSFUNCTION: "Erectile dysfunction (impotence)",
  ESCHERICHIA_COLI: "Escherichia coli (E. coli) O157",
  EWING_SARCOMA_CHILDREN: "Ewing sarcoma: Children",
  GASTRO_OESOPHAGEAL_REFLUX_DISEASE: "Gastro-oesophageal reflux disease (GORD)",
  HAEMORRHOIDS: "Haemorrhoids (piles)",
  HODGKIN_LYMPHOMA_CHILDREN: "Hodgkin lymphoma: Children",
  HODGKIN_LYMPHOMA_TEENAGERS_AND_YOUNG_ADULTS:
    "Hodgkin lymphoma: Teenagers and young adults",
  HUNTINGTONS_DISEASE: "Huntington's disease",
  HYPERGLYCAEMIA: "Hyperglycaemia (high blood sugar)",
  HYPERHIDROSIS: "Hypoglycaemia (low blood sugar)",
  HYPOGLYCAEMIA: "Irritable bowel syndrome (IBS)",
  IRRITABLE_BOWEL_SYNDROME: "Kaposi's sarcoma",
  KAPOSIS_SARCOMA: "Laryngeal (larynx) cancer",
  LARYNGEAL_CANCER: "Lymphogranuloma venereum (LGV)",
  LYMPHOGRANULOMA_VENEREUM: "Malignant brain tumour (cancerous)",
  MALIGNANT_BRAIN_TUMOUR: "Middle ear infection (otitis media)",
  MENIERES_DISEASE: "Motor neurone disease (MND)",
  MIDDLE_EAR_INFECTION: "Multiple sclerosis (MS)",
  MOTOR_NEURONE_DISEASE: "Meniere's disease",
  MULTIPLE_SCLEROSIS: "Neuroblastoma: Children",
  NEUROBLASTOMA_CHILDREN: "Non-alcoholic fatty liver disease (NAFLD)",
  NON_ALCOHOLIC_FATTY_LIVER_DISEASE: "Non-Hodgkin lymphoma",
  NON_HODGKIN_LYMPHOMA: "Non-Hodgkin lymphoma: Children",
  NON_HODGKIN_LYMPHOMA_CHILDREN: "Obsessive compulsive disorder (OCD)",
  OBSESSIVE_COMPULSIVE_DISORDER: "Ovarian cancer: Teenagers and young adults",
  OVARIAN_CANCER_TEENAGERS_AND_YOUNG_ADULTS: "Paget's disease of the nipple",
  PAGETS_DISEASE_OF_THE_NIPPLE: "Parkinson's disease",
  PARKINSONS_DISEASE: "Post-traumatic stress disorder (PTSD)",
  POST_TRAUMATIC_STRESS_DISORDER: "Raynaud's phenomenon",
  RAYNAUDS_PHENOMENON: "Retinoblastoma: Children",
  RETINOBLASTOMA_CHILDREN: "Sjogren's syndrome",
  SJOGRENS_SYNDROME: "Skin cancer (melanoma)",
  SKIN_CANCER_MELANOMA: "Skin cancer (non-melanoma)",
  SKIN_CANCER_NON_MELANOMA: "Soft tissue sarcomas: Teenagers and young adults",
  SOFT_TISSUE_SARCOMAS_TEENAGERS_AND_YOUNG_ADULTS:
    "Sudden infant death syndrome (SIDS)",
  SUDDEN_INFANT_DEATH_SYNDROME: "Testicular cancer: Teenagers and young adults",
  TESTICULAR_CANCER_TEENAGERS_AND_YOUNG_ADULTS:
    "Thyroid cancer: Teenagers and young adults",
  TESTICULAR_LUMPS_AND_SWELLINGS: "Transient ischaemic attack (TIA)",
  TUBERCULOSIS: "Tuberculosis (TB)",
  URINARY_TRACT_INFECTION: "Urinary tract infection (UTI)",
  URINARY_TRACT_INFECTION_IN_CHILDREN:
    "Urinary tract infection (UTI) in children",
  URTICARIA: "Urticaria (hives)",
  WILMS_TUMOUR: "Wilms’ tumour",
  WOMB_CANCER: "Womb (uterus) cancer",
};

function validateDiseaseTypes(value: string) {
  let disease = capitalize(startCase(value));
  if (value in diseaseTypes) {
    disease = diseaseTypes[value];
  }
  return disease;
}

const options = mapValues(keyBy(DiseaseKind), (value) =>
  validateDiseaseTypes(value)
);

const validationSchema = yup
  .object({
    medicalHistoryFields: yup
      .object({
        diseases: yup
          .array<DiseaseKind>(
            yup.mixed<DiseaseKind>().oneOf(Object.values(DiseaseKind))
          )
          .nullable(),
        medications: yup
          .array()
          .of(yup.string().required())
          .nullable()
          .label("Medications"),
        medicalNotes: yup.string().nullable().label("Medical Notes"),
        personalHistory: yup.string().nullable().label("Personal history"),
        familyHistory: yup.string().nullable().label("Family history"),
        otherMedicalInfo: yup.string().nullable().label("Other medical info"),
      })
      .defined(),
    pregnancyHistoryFields: yup
      .object({
        numberOfPregnancies: yup
          .number()
          .nullable()
          .min(0)
          .integer()
          .typeError("${label} must be a number")
          .label("Pregnancy count"),
        currentlyPregnant: yup.boolean().optional(),
        currentlyLactating: yup.boolean().optional(),
        lastMenstruationOn: yup
          .date()
          .nullable()
          .label("Start of last menstruation"),
        gestationalType: yup
          .string()
          .nullable()
          .oneOf([...Object.values(GestationalKind), null])
          .label("Gestational type"),
        lactationStartsOn: yup.date().nullable().label("Start of lactation"),
        lactationDurationMonths: yup
          .number()
          .nullable()
          .min(0)
          .integer()
          .typeError("${label} must be a number")
          .label("Lactation duration"),
        pregnancyObservation: yup.string().nullable().label("Observation"),
      })
      .defined(),
  })
  .defined();

export type FormValues = ReturnType<typeof validationSchema.cast>;

export const getDiseaseLabel = (value: keyof typeof options): string => {
  if (!value) return "";
  return options[value];
};

const updateMedicalHistoryMutation = graphql(`
  mutation UpdateMedicalHistory($input: UpdateMedicalHistoryInput!) {
    updateMedicalHistory(input: $input) {
      medicalHistory {
        ...ClientMedicalHistory
      }
    }
  }
`);

const updatePregnancyHistoryMutation = graphql(`
  mutation UpdatePregnancyHistory($input: UpdatePregnancyHistoryInput!) {
    updatePregnancyHistory(input: $input) {
      pregnancyHistory {
        ...ClientPregnancyHistory
      }
    }
  }
`);

const medicationsQuery = graphql(`
  query Medications($searchString: String!) {
    drugs(search: $searchString) {
      id
      brandName
    }
  }
`);

const EditMedicalHistoryModal: React.FC<EditMedicalHistoryModalProps> = ({
  visible,
  onClose,
  gender,
  medicalHistory,
  pregnancyHistory,
}) => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const modalVisibility = visible !== undefined ? visible : false;

  const methods = useForm<FormValues>({
    defaultValues: {
      medicalHistoryFields: {
        diseases: [],
        medications: [],
        familyHistory: "",
        medicalNotes: "",
        otherMedicalInfo: "",
        personalHistory: "",
      },
      pregnancyHistoryFields: {
        numberOfPregnancies: null,
        currentlyPregnant: false,
        currentlyLactating: false,
        gestationalType: null,
        lactationStartsOn: null,
        lactationDurationMonths: null,
        pregnancyObservation: "",
      },
    },
    mode: "onBlur",
    reValidateMode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });
  const [medicationListSearchText, setMedicationListSearchText] =
    useState<string>("");
  const [allMedicalList, setAllMedicalList] = useState<string[]>([]);
  const setMedicationListSearch = useDebounce(setMedicationListSearchText, 500);

  const { handleSubmit, control, setValue, watch } = methods;

  useEffect(() => {
    if (pregnancyHistory != null) {
      if (gender === Gender.Female) {
        setValue("pregnancyHistoryFields", {
          currentlyLactating: pregnancyHistory.currentlyLactating ?? false,
          currentlyPregnant: pregnancyHistory.currentlyPregnant ?? false,
          gestationalType:
            pregnancyHistory.gestationalType ?? GestationalKind.Single,
          lactationDurationMonths:
            pregnancyHistory.lactationDurationMonths ?? 0,
          lactationStartsOn: pregnancyHistory.lactationStartsOn
            ? new Date(pregnancyHistory.lactationStartsOn)
            : new Date(),
          lastMenstruationOn: pregnancyHistory.lastMenstruationOn
            ? new Date(pregnancyHistory.lastMenstruationOn)
            : new Date(),
          numberOfPregnancies: pregnancyHistory.numberOfPregnancies ?? 0,
          pregnancyObservation: pregnancyHistory.pregnancyObservation ?? "",
        });
      }
    }
    if (medicalHistory != null) {
      setValue("medicalHistoryFields", {
        diseases:
          (medicalHistory.diseases?.map(toUpper) as DiseaseKind[]) ?? [],
        medications: medicalHistory.medications ?? [],
        familyHistory: medicalHistory.familyHistory ?? "",
        medicalNotes: medicalHistory.medicalNotes ?? "",
        otherMedicalInfo: medicalHistory.otherMedicalInfo ?? "",
        personalHistory: medicalHistory.personalHistory ?? "",
      });
    }
  }, [gender, medicalHistory, pregnancyHistory]);

  const [updateMedicalHistory] = useMutation(updateMedicalHistoryMutation);
  const [updatePregnancyHistory] = useMutation(updatePregnancyHistoryMutation);
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();
  const medicationArray = watch("medicalHistoryFields.medications") || [];

  const showSnackBar = (isSuccessful: boolean, message?: string) => {
    if (isSuccessful) {
      snackBarHelper.open(message ?? "Successfully updated");
    } else {
      snackBarHelper.open(
        message ?? "There is an error in updating your clients medical history"
      );
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const medicalHistoryFields = formValues?.medicalHistoryFields;
    const pregnancyHistoryFields = formValues?.pregnancyHistoryFields;

    try {
      if (gender === Gender.Female) {
        const pregnancyHistoryInput: UpdatePregnancyHistoryInput = {
          clientId,
          attributes: {
            gestationalType: pregnancyHistoryFields.gestationalType,
            currentlyLactating: pregnancyHistoryFields.currentlyLactating,
            currentlyPregnant: pregnancyHistoryFields.currentlyPregnant,
            lactationStartsOn: apply(
              pregnancyHistoryFields.lactationStartsOn,
              formatISODate
            ),
            lastMenstruationOn: apply(
              pregnancyHistoryFields.lastMenstruationOn,
              formatISODate
            ),
            lactationDurationMonths:
              pregnancyHistoryFields.lactationDurationMonths,
            numberOfPregnancies: Number(
              pregnancyHistoryFields.numberOfPregnancies
            ),
            pregnancyObservation: pregnancyHistoryFields.pregnancyObservation,
          },
        };
        await updatePregnancyHistory({
          variables: { input: pregnancyHistoryInput },
        });
      }

      const medicalHistoryInput: UpdateMedicalHistoryInput = {
        clientId,
        attributes: {
          diseases: medicalHistoryFields?.diseases,
          familyHistory: medicalHistoryFields?.familyHistory,
          medicalNotes: medicalHistoryFields?.medicalNotes,
          medications: medicalHistoryFields?.medications,
          otherMedicalInfo: medicalHistoryFields?.otherMedicalInfo,
          personalHistory: medicalHistoryFields?.personalHistory,
        },
      };

      const result = await updateMedicalHistory({
        variables: { input: medicalHistoryInput },
      });

      if (result.data) {
        showSnackBar(true);
        onClose();
      }
    } catch (e) {
      const message = displayError(e);
      showSnackBar(false, message);
    }
  };

  const renderSelectedDiseases = (selected: string[]) => {
    return selected?.map((e: string) => (
      <Chip
        key={e}
        label={getDiseaseLabel(e as keyof typeof options)}
        className={classes.chip}
      />
    ));
  };

  const searchMedications = () => {
    client
      .query({
        query: medicationsQuery,
        variables: {
          searchString: medicationListSearchText.toUpperCase(),
        },
      })
      .then((result) => {
        setAllMedicalList(result.data.drugs.map((e) => e.brandName));
      });
  };

  useLayoutEffect(() => {
    if (medicationListSearchText === "") {
      setAllMedicalList([]);
    } else {
      searchMedications();
    }
  }, [medicationListSearchText]);

  function handleAddMedication(medication: string) {
    setValue("medicalHistoryFields.medications", [
      ...medicationArray,
      medication,
    ]);
  }

  return (
    <>
      <Modal open={modalVisibility} onClose={onClose} className={classes.root}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Edit Medical History
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {gender === Gender.Female && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        className={classes.modalSubheaderText}
                        color="textSecondary"
                      >
                        Pregnancy History
                      </Typography>
                    </Grid>
                    <PregnancyHistoryFields />
                  </>
                )}
              </Grid>

              <Box marginBottom={5} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    className={classes.modalSubheaderText}
                    color="textSecondary"
                  >
                    Ailments/illnesses (if any)
                  </Typography>
                </Grid>

                <Controller
                  name="medicalHistoryFields.diseases"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Typography>Disease Types</Typography>
                        <StyledFormControl variant="outlined" fullWidth>
                          <StyledInputLabel>Select Diseases</StyledInputLabel>
                          <Select
                            {...field}
                            multiple
                            input={<OutlinedInput label="Chip" />}
                            renderValue={(selected: unknown) => (
                              <div className={classes.chipWrapper}>
                                {renderSelectedDiseases(selected as string[])}
                              </div>
                            )}
                            inputProps={{ "aria-label": "Without label" }}
                            onChange={({ target: { value } }) =>
                              field.onChange(value as DiseaseKind[])
                            }
                          >
                            {Object.entries(options).map(([key, value]) => (
                              <MenuItem key={key} value={key}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                        <Typography
                          variant="subtitle1"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </Grid>
                    );
                  }}
                />
                <Controller
                  name="medicalHistoryFields.medications"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography>Medications</Typography>
                          {allMedicalList.length == 0 &&
                            medicationListSearchText !== "" && (
                              <AddIconButton
                                onClick={() =>
                                  handleAddMedication(medicationListSearchText)
                                }
                              />
                            )}
                        </Box>
                        <Autocomplete
                          {...field}
                          multiple
                          options={allMedicalList.map((option) =>
                            humanize(option)
                          )}
                          renderTags={(value: string[], getTagProps) =>
                            value.map((option: string, index: number) => (
                              // eslint-disable-next-line react/jsx-key
                              <Chip
                                variant="outlined"
                                label={option}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              variant="outlined"
                              placeholder="Medications"
                              onChange={(e) => {
                                setMedicationListSearch(e.target.value);
                              }}
                            />
                          )}
                          value={field.value || []}
                          onChange={(_, newValue) => field.onChange(newValue)}
                        />
                      </Grid>
                    );
                  }}
                />

                <Controller
                  name="medicalHistoryFields.medicalNotes"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Typography>Medical Notes</Typography>
                        <StyledTextField {...field} multiline />
                        <Typography
                          variant="subtitle1"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </Grid>
                    );
                  }}
                />
              </Grid>

              <Box marginBottom={5} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    className={classes.modalSubheaderText}
                    color="textSecondary"
                  >
                    Others
                  </Typography>
                </Grid>

                <Controller
                  name="medicalHistoryFields.personalHistory"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Typography>Personal History</Typography>
                        <StyledTextField {...field} multiline />
                        <Typography
                          variant="subtitle1"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </Grid>
                    );
                  }}
                />

                <Controller
                  name="medicalHistoryFields.familyHistory"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Typography>Family History</Typography>
                        <StyledTextField {...field} multiline />
                        <Typography
                          variant="subtitle1"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </Grid>
                    );
                  }}
                />

                <Controller
                  name="medicalHistoryFields.otherMedicalInfo"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12} lg={6}>
                        <Typography>Other Medical Info</Typography>
                        <StyledTextField {...field} multiline />
                        <Typography
                          variant="subtitle1"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </Grid>
                    );
                  }}
                />
              </Grid>

              <div className={classes.save}>
                <StyledSaveButton type="submit">Update</StyledSaveButton>
              </div>
            </form>
          </FormProvider>
        </Box>
      </Modal>
      <SimpleSnackBar />
    </>
  );
};
export default EditMedicalHistoryModal;
